import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';

import { DashboardService, DashboardFilter, MultiselectType } from '../../services/dashboard.service';
import { ReportsService } from '../../services/reports.service';
import { CommonService } from '../../services/common.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'vrm-analytics-chart-filter',
  templateUrl: './vrm-analytics-chart-filter.component.html',
  styleUrls: ['./vrm-analytics-chart-filter.component.scss'],
})
export class VrmAnalyticsChartFilterComponent implements OnInit, OnDestroy {
  dashboardFilters: Array<DashboardFilter> = [];
  dashboardDropdownSettings: Map<string, any>;
  locations: Array<DashboardFilter> = [];
  locationDropdownSettings: any;
  hasFilters: boolean = true;

  filterMenuHidden: boolean = true;
  filterLoading = false;
  filterCount = 0;
  filterRecalculationInProgress: boolean = true;

  private subscriptions = {
    dashboardFilters: null,
  };

  constructor(
    public dashboardService: DashboardService,
    public cService: CommonService,
    private rptService: ReportsService,
    private changeDetector: ChangeDetectorRef
  ) {
    this.locationDropdownSettings = {
      singleSelection: false,
      text: this.cService.readyTranslations.generic.select,
      selectAllText: this.cService.readyTranslations.generic.selectAll,
      unSelectAllText: this.cService.readyTranslations.generic.unselectAll,
      badgeShowLimit: 4,
      enableSearchFilter: true,
      primaryKey: 'itemName',
      tagToBody: false,
      lazyLoading: true,
      maxHeight: 200,
    };
  }

  ngOnInit(): void {
    this.initializeOrRecalculateFilters();

    this.subscriptions.dashboardFilters = this.dashboardService.dashboardSpecificFiltersChanged$.subscribe({
      next: () => {
        this.dashboardFilters = this.dashboardService.currentDashboardSpecificFilters;
        this.refreshFilterCount();
        this.changeDetector.detectChanges();
      },
    });
  }

  private initializeOrRecalculateFilters(
    recalculate: boolean = false,
    done?: (response: Array<DashboardFilter>) => void
  ) {
    this.filterLoading = true;
    this.filterMenuHidden = recalculate ? false : true;
    this.filterRecalculationInProgress = recalculate;

    const currentSelection = recalculate ? this.convertSelectedFiltersToPostData() : {};

    this.dashboardService
      .getDashboardLocationFilters(currentSelection)
      .subscribe((response: Array<DashboardFilter>) => {
        if (!recalculate) {
          this.assignTransformedValues(response);
          this.dashboardService.mergePersistedGridLocationValuesToActiveDashboardFilters();
        }

        this.dashboardService.triggerFiltersLoadedOrRecalculated(recalculate);

        this.hasFilters = (response ?? []).length > 0 || this.dashboardFilters.length > 0;
        this.filterLoading = false;
        if (done) {
          done(response);
        }
        this.refreshFilterCount();
        this.filterRecalculationInProgress = false;
      });
  }

  private assignTransformedValues(locations: Array<DashboardFilter>, merge?: boolean) {
    const resetActiveFilters = merge ? false : true;
    const transformedLocation = this.transformFilterData(locations, resetActiveFilters);

    if (merge) {
      this.locations.forEach((currentLoc: DashboardFilter) => {
        const newLocValues = transformedLocation.find((loc) => loc.location === currentLoc.location);
        currentLoc.locationValues = newLocValues.locationValues;
        currentLoc.transformedValues = newLocValues.transformedValues;
      });
    } else {
      this.locations = transformedLocation;
    }

    this.dashboardService.allAvailableDashboardFilters = [...this.dashboardFilters, ...this.locations];
  }

  private mergeActiveLocationFiltersToPersistedGridState() {
    const activeFilters = this.dashboardService.activeDashboardFilters;
    const gridFilterState = this.getGridStateWithoutLocationFilters();

    for (const filterKey in activeFilters) {
      if (filterKey) {
        const filterValues: Array<MultiselectType> = this.dashboardService.activeDashboardFilters[filterKey];

        if (filterValues.length) {
          gridFilterState[filterKey] = {
            filterType: 'set',
            values: filterValues.map((val) => (val.itemName === '(Blanks)' ? null : val.itemName)),
          };
        }
      }
    }

    this.rptService.gridState.filterModel = gridFilterState;
    this.rptService.cacheFiltersAcrossClientInstances();
  }

  private getGridStateWithoutLocationFilters(cacheNewStateWithoutLocations: boolean = false) {
    const cachedFilterModel = this.rptService.getCachedFilters();
    const gridStateWithoutLocationFilters = {};

    if (cachedFilterModel && Object.keys(cachedFilterModel).length) {
      for (const filterKey in cachedFilterModel) {
        if (filterKey && !filterKey.includes('location')) {
          const nonLocationFilter = cachedFilterModel[filterKey];
          gridStateWithoutLocationFilters[filterKey] = nonLocationFilter;
        }
      }
    }

    if (cacheNewStateWithoutLocations) {
      this.rptService.gridState.filterModel = gridStateWithoutLocationFilters;
      this.rptService.cacheFiltersAcrossClientInstances();
    }

    return gridStateWithoutLocationFilters;
  }

  private transformFilterData(
    filters: Array<DashboardFilter> = [],
    resetActiveFilters?: boolean
  ): Array<DashboardFilter> {
    filters.forEach((filter: DashboardFilter) => {
      if (filter.locationValues && filter.locationValues.length) {
        filter.transformedValues = [];

        filter.locationValues.forEach((value: string, key: number) => {
          filter.transformedValues.push({ id: key + 1, itemName: value });
        });
      }
    });

    if (resetActiveFilters) {
      this.dashboardService.activeDashboardFilters = {};
    }

    return filters;
  }

  private convertSelectedFiltersToPostData(): unknown {
    const activeFilters = this.dashboardService.activeDashboardFilters;
    const convertedActiveFilters = {};

    for (const locationKey in activeFilters) {
      if (locationKey) {
        const location = activeFilters[locationKey] as Array<{ id: number; itemName: string }>;
        if (location.length) {
          convertedActiveFilters[locationKey] = location.map((loc) => loc.itemName);
        }
      }
    }

    return convertedActiveFilters;
  }

  filterSelectedOrDeselected() {
    this.filterRecalculationInProgress = true;

    this.initializeOrRecalculateFilters(true, (response: Array<DashboardFilter>) => {
      this.assignTransformedValues(response, true);
    });
  }

  filterDeselectedAll(itemDeselected: DashboardFilter) {
    if (itemDeselected.defaultValue) {
      const defaultValue = itemDeselected.transformedValues.find(
        (item) => item?.itemName?.toLowerCase() === itemDeselected?.defaultValue?.toLowerCase()
      );
      this.dashboardService.activeDashboardFilters[itemDeselected.location] = [defaultValue];
    }
  }

  toggleFilterMenu() {
    this.filterMenuHidden = !this.filterMenuHidden;
  }

  applyFilter() {
    this.toggleFilterMenu();
    this.refreshFilterCount();
    this.mergeActiveLocationFiltersToPersistedGridState();
    this.dashboardService.triggerDashboardFilterChange();
  }

  resetFilter() {
    this.dashboardService.resetFilters();
    this.applyFilter();
    this.getGridStateWithoutLocationFilters(true);
  }

  private refreshFilterCount(): void {
    this.filterCount = this.currentFilterCount(this.dashboardService.activeDashboardFilters);
  }

  private currentFilterCount(selectedFilters: any) {
    let filterCount = 0;

    for (const objTag in selectedFilters) {
      if (objTag && this.dashboardService.existsFilterInAvailableFilters(objTag)) {
        const location = selectedFilters[objTag];
        filterCount = filterCount + location.length;
      }
    }

    return filterCount;
  }

  ngOnDestroy(): void {
    for (const sub in this.subscriptions) {
      if (sub) {
        const subscription: Subscription = this.subscriptions[sub];

        if (subscription) {
          subscription.unsubscribe();
        }
      }
    }
  }
}
